import { Injectable } from "@angular/core";
import { BudgetStatusEnum, ClientApproverProfileEnum } from "@shared/enums";
import { KeycloakHelperService } from "./keycloak.helper.service";
import moment from "moment";
import { ServiceRequestDetailsDTO } from "@shared/models/service-request-details-dto";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { NotifyService } from "./notify.service";
import {
  ModalApprovalComponent,
  RescheduleModalComponent
} from "@shared/components";

type ApprovalAction =
  | "rescheduleToSameCarWorkshopWithApprovalCode"
  | "rescheduleToSameCarWorkshopWithoutApprovalCode"
  | "approveWithoutApprovalCode"
  | "approveWithApprovalCode"
  | "cannotApprove";

@Injectable({
  providedIn: "root"
})
export class BudgetApprovalService {
  constructor(
    private authService: KeycloakHelperService,
    private dialog: MatDialog,
    private notifyService: NotifyService
  ) {}

  private isClientApprover(
    clientApproverProfile: ClientApproverProfileEnum
  ): boolean {
    const isDriverApprover =
      clientApproverProfile === ClientApproverProfileEnum.DRIVER_APPROVER &&
      this.authService.isDriverApproverLogged();

    const isFleetAdminApprover =
      clientApproverProfile ===
        ClientApproverProfileEnum.FLEET_ADMIN_APPROVER &&
      this.authService.isFleetAdminLogged();

    return (
      isDriverApprover || isFleetAdminApprover || clientApproverProfile === null
    );
  }

  private canRescheduleAndDateIsBeforeToday(
    date: Date,
    budgetStatus: BudgetStatusEnum
  ): boolean {
    return (
      moment.utc(date).format("YYYY-MM-DD") <
        moment.utc().format("YYYY-MM-DD") &&
      budgetStatus === BudgetStatusEnum.BUDGET_FLEET_ADMIN_APPROVAL
    );
  }

  /**
   * @param clientApproverProfile Type of client who will approve the budget
   * @param date Service request's scheduling date
   * @param budgetStatus Status of Budget
   * @returns The action to be triggered by the component.
   */
  getApprovalAction(
    clientApproverProfile: ClientApproverProfileEnum,
    date: Date,
    budgetStatus: BudgetStatusEnum,
    isLastApprover: boolean
  ): ApprovalAction {
    const canApproveBudget =
      budgetStatus !== BudgetStatusEnum.BUDGET_FLEET_ADMIN_APPROVAL ||
      this.isClientApprover(clientApproverProfile);
    if (
      this.canRescheduleAndDateIsBeforeToday(date, budgetStatus) &&
      isLastApprover
    ) {
      return "rescheduleToSameCarWorkshopWithApprovalCode";
    }

    if (
      this.canRescheduleAndDateIsBeforeToday(date, budgetStatus) &&
      !isLastApprover
    ) {
      return "rescheduleToSameCarWorkshopWithoutApprovalCode";
    }

    if (canApproveBudget && isLastApprover) {
      return "approveWithApprovalCode";
    }

    if (canApproveBudget && !isLastApprover) {
      return "approveWithoutApprovalCode";
    }

    return "cannotApprove";
  }

  rescheduleToSameCarWorkshop(
    dto: ServiceRequestDetailsDTO
  ): MatDialogRef<RescheduleModalComponent, unknown> {
    this.notifyService.showInformationMessageWithTranslationKey(
      "date-confirmed-expired"
    );

    return this.dialog.open(RescheduleModalComponent, {
      width: "1000px",
      data: {
        clientId: dto.client.id,
        vehicleId: dto.vehicle.id,
        vehicleCategory: dto.vehicle.modelCategory,
        serviceType: dto.serviceType,
        view: "RESCHEDULE_DATE_MODAL",
        carWorkshopId: dto.carWorkshop.id,
        daysAdvanceForAppointments: dto.client.daysAdvanceForAppointments,
        isBackofficeUserLogged:
          this.authService.isBackofficeOperationLogged() ||
          this.authService.isBackofficeExternalLogged() ||
          this.authService.isBackofficeApproverManagerLogged() ||
          this.authService.isBackofficeAdministratorLogged()
      },
      panelClass: "reschedule-dialog-container"
    });
  }

  openApproveBudgetDialog(): MatDialogRef<ModalApprovalComponent> {
    return this.dialog.open(ModalApprovalComponent, { width: "400px" });
  }

  /**
   * Check if the 'Approve' and 'Disapprove' buttons can appear to the user.
   * The buttons can appear only when
   * - Status of the budget is BUDGET_CREATED
   * - User is a client approver
   * - Budget is in the new preventive flow.
   *
   * @param {BudgetStatusEnum} budgetStatus The status of the budget
   * @param {boolean} isNewPreventiveFlow
   * Indicate whether the budget is part of the new preventive flow.
   *
   * @returns {boolean} Show 'Approve' and 'Disapprove' buttons if true.
   * Otherwise false.
   */
  shouldShowApproveDisapproveButtons(
    budgetStatus: BudgetStatusEnum,
    isNewPreventiveFlow: boolean
  ): boolean {
    const isApprover =
      this.authService.isDriverApproverLogged() ||
      this.authService.isFleetAdminLogged();

    const validBudgetStatus =
      budgetStatus === BudgetStatusEnum.BUDGET_CREATED ||
      budgetStatus === BudgetStatusEnum.BUDGET_PENDING_CLIENT_APPROVAL;

    return validBudgetStatus && isNewPreventiveFlow && isApprover;
  }

  /**
   * Check if the 'Return to Analysis' button can appear to the user.
   * The buttons can appear only when
   * - Status of the budget is BUDGET_FLEET_ADMIN_APPROVAL
   * - User is a client approver
   *
   * @param {BudgetStatusEnum} budgetStatus The status of the budget
   * @returns {boolean} Show 'Return to Analysis' button if true.
   * Otherwise false.
   */
  shouldShowAnalysisButton(budgetStatus: BudgetStatusEnum): boolean {
    const isApprover =
      this.authService.isDriverApproverLogged() ||
      this.authService.isFleetAdminLogged();

    return (
      budgetStatus === BudgetStatusEnum.BUDGET_FLEET_ADMIN_APPROVAL &&
      isApprover
    );
  }
}
